import { RotateCcw } from "lucide-react";
import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ResetCardMenuProps {
  onResetOrder?: () => void;
  onResetMetricFilters?: () => void;
  onResetAll: () => void;
}

export const ResetCardMenu: React.FC<ResetCardMenuProps> = ({
  onResetOrder,
  onResetMetricFilters,
  onResetAll,
}) => {
  const [open, setOpen] = React.useState(false);
  const [resetOrder, setResetOrder] = React.useState(false);
  const [resetMetricFilters, setResetMetricFilters] = React.useState(false);

  const clearSelections = React.useCallback(() => {
    setResetOrder(false);
    setResetMetricFilters(false);
  }, []);

  const applyResets = React.useCallback(() => {
    if (resetOrder) onResetOrder?.();
    if (resetMetricFilters) onResetMetricFilters?.();
    setOpen(false);
    clearSelections();
  }, [
    resetOrder,
    resetMetricFilters,
    onResetOrder,
    onResetMetricFilters,
    clearSelections,
  ]);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        if (!o) clearSelections();
        setOpen(o);
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Reset options</p>
        </TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onResetAll();
            setOpen(false);
          }}
        >
          Reset all
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {onResetOrder && (
          <DropdownMenuCheckboxItem
            checked={resetOrder}
            onCheckedChange={setResetOrder}
            onSelect={(e) => e.preventDefault()}
          >
            Reset card order
          </DropdownMenuCheckboxItem>
        )}
        {onResetMetricFilters && (
          <DropdownMenuCheckboxItem
            checked={resetMetricFilters}
            onCheckedChange={setResetMetricFilters}
            onSelect={(e) => e.preventDefault()}
          >
            Reset metrics filters
          </DropdownMenuCheckboxItem>
        )}
        <div className="border-t mt-2 pt-2 px-2 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={clearSelections}>
            Clear
          </Button>
          <Button
            size="sm"
            onClick={applyResets}
            disabled={!(resetOrder || resetMetricFilters)}
          >
            Apply
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ResetCardMenu;
